import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BudgetTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await fetch(`${API}/templates`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const handleApplyTemplate = async (templateId) => {
    setApplying(templateId);
    try {
      const response = await fetch(`${API}/templates/${templateId}/apply`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}\n\nCreated ${data.created_bills.length} bills and ${data.created_income_templates.length} income templates!`);
        setShowConfirm(null);
        navigate('/dashboard');
      } else {
        alert('Failed to apply template');
      }
    } catch (error) {
      console.error('Error applying template:', error);
      alert('Error applying template');
    } finally {
      setApplying(null);
    }
  };

  const getTemplateIcon = (templateId) => {
    const icons = {
      single_income: '👤',
      dual_income: '👫',
      student: '🎓',
      family: '👨‍👩‍👧‍👦',
      minimalist: '🌿',
    };
    return icons[templateId] || '📋';
  };

  const getTemplateColor = (templateId) => {
    const colors = {
      single_income: 'from-blue-500 to-indigo-500',
      dual_income: 'from-purple-500 to-pink-500',
      student: 'from-green-500 to-teal-500',
      family: 'from-amber-500 to-orange-500',
      minimalist: 'from-gray-500 to-gray-600',
    };
    return colors[templateId] || 'from-gray-500 to-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
          <span className="text-3xl">📋</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="templates-title">Budget Templates</h1>
        <p className="text-gray-600 mt-2">
          Start with a pre-made budget template to save time
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <p className="font-medium text-amber-800">How Templates Work</p>
            <p className="text-sm text-amber-700 mt-1">
              Templates add sample bills and income sources to your account. You can then customize 
              the amounts, add or remove items, and adjust due dates to match your actual situation.
            </p>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {templates.map((template) => (
          <div
            key={template.id}
            className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${getTemplateColor(template.id)} text-white p-6`}>
              <div className="flex items-center gap-3">
                <span className="text-4xl">{getTemplateIcon(template.id)}</span>
                <div>
                  <h3 className="text-xl font-bold">{template.name}</h3>
                  <p className="text-white/80 text-sm">{template.description}</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Bills Preview */}
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  📝 Bills ({template.bills.length})
                </h4>
                <div className="flex flex-wrap gap-1">
                  {template.bills.slice(0, 6).map((bill, i) => (
                    <span
                      key={i}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                    >
                      {bill.name}
                    </span>
                  ))}
                  {template.bills.length > 6 && (
                    <span className="text-xs text-gray-500">
                      +{template.bills.length - 6} more
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Total: ~${template.bills.reduce((sum, b) => sum + b.amount, 0).toLocaleString()}/month
                </p>
              </div>

              {/* Income Preview */}
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  💵 Income Templates ({template.income_templates.length})
                </h4>
                <div className="space-y-1">
                  {template.income_templates.map((inc, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-600">{inc.name}</span>
                      <span className="font-medium">${inc.default_amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Apply Button */}
              {showConfirm === template.id ? (
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-sm text-amber-800 mb-3">
                    This will add {template.bills.length} bills and {template.income_templates.length} income templates to your account. Continue?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApplyTemplate(template.id)}
                      disabled={applying === template.id}
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                    >
                      {applying === template.id ? 'Applying...' : 'Yes, Apply'}
                    </button>
                    <button
                      onClick={() => setShowConfirm(null)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowConfirm(template.id)}
                  className="w-full py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
                  data-testid={`apply-template-${template.id}`}
                >
                  Use This Template
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Back Link */}
      <div className="text-center pt-4">
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">
          ← Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

export default BudgetTemplates;
